import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { templatesApi } from '@/features/DataHub/api/templates.api';
import { useSelectedSymbols } from '@/features/DataHub/hooks/useSelectedSymbols';
import { templateKeys } from '@/features/DataHub/queries/keys/templates.keys';

import useDownloadFileContent from './useDownloadFileContent';
import { useLatestApiKey } from './useLatestApiKey';
import { powerQueryPreviewService } from '../services';

const usePowerQueryPreview = (
  capturePosthogEvent: () => void,
  exportApiUrl: string,
  enabled = true
) => {
  const symbols = useSelectedSymbols();
  const apiKey = useLatestApiKey() ?? '<YourApiKeyHere>';

  const { downloadFileContent } = useDownloadFileContent(capturePosthogEvent);

  // Fetch the static Power Query template once; do not refetch on input changes
  const {
    data: template,
    isLoading: loading,
    isError: error
  } = useQuery({
    queryKey: templateKeys.detail('powerQuery'),
    enabled,
    queryFn: () => templatesApi.fetchPowerQueryTemplate(),
    staleTime: Infinity
  });

  const fileContent = useMemo(() => {
    if (!enabled || !template || !exportApiUrl) return '';

    // Use the service to generate preview
    const result = powerQueryPreviewService.generateFromTemplate(
      template,
      {
        symbols,
        rangeStart: '', // Not needed for Power Query
        rangeEnd: '',
        timezone: '',
        includeMetadata: true,
        apiKey,
        gxURL: ''
      },
      exportApiUrl
    );

    return result.content;
  }, [enabled, template, exportApiUrl, symbols, apiKey]);

  const downloadFile = useCallback(() => {
    if (fileContent) {
      downloadFileContent(fileContent, 'txt');
    }
  }, [fileContent, downloadFileContent]);

  return { loading, error, fileContent, downloadFile };
};

export default usePowerQueryPreview;
